import { useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { UniversalAction } from '../../universal-flow/types';
import { buildFieldSchema, type FieldSchema } from '../../universal-flow/form';
import type { LocaleKey } from '../../i18n';

type Props = {
  action: UniversalAction;
  submitting: boolean;
  onSubmit: (payload: Record<string, string>) => Promise<void>;
  labels: {
    submit: string;
    selectPlaceholder: string;
    validationFallback: string;
    pending: string;
    invalidFormat: string;
    processing: string;
    /** Optional map: legend text (value from backend) -> translated legend */
    formLegendTranslations?: Record<string, string>;
    /** Optional map: description text (value from backend) -> translated description */
    formDescriptionTranslations?: Record<string, string>;
  };
  initialValues?: Record<string, unknown>;
  /** Текущая локаль; для Sentry при отсутствии перевода */
  locale?: LocaleKey;
};

function reportMissingTranslation(
  locale: LocaleKey,
  untranslatedText: string,
  kind: 'legend' | 'description',
  reportedSet: Set<string>
): void {
  const key = `${locale}:${kind}:${untranslatedText}`;
  if (reportedSet.has(key)) return;
  reportedSet.add(key);
  Sentry.withScope((scope) => {
    scope.setTag('missing_translation', 'true');
    scope.setTag('locale', locale);
    scope.setContext('translation', {
      untranslated_text: untranslatedText,
      kind
    });
    Sentry.captureMessage(`Missing translation [${locale}] (${kind}): "${untranslatedText.slice(0, 80)}${untranslatedText.length > 80 ? '…' : ''}"`, 'info');
  });
}

export function ActionForm({ action, submitting, onSubmit, labels, initialValues, locale }: Props) {
  if (action.type !== 'form' || action.fields === null || action.fields === undefined) {
    throw new Error('ActionForm expects action.type === "form" with defined fields');
  }

  const schemas = useMemo<FieldSchema[]>(() => {
    if (action.fields === null || action.fields === undefined) {
      return [];
    }
    return Object.entries(action.fields).map(([name, definition]) => buildFieldSchema(name, definition));
  }, [action.fields]);

  const [values, setValues] = useState<Record<string, string>>(() =>
    buildInitialValues(schemas, initialValues)
  );
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const reportedMissingRef = useMemo(() => ({ current: new Set<string>() }), []);

  useEffect(() => {
    setValues((prev) => ({
      ...buildInitialValues(schemas, initialValues, prev)
    }));
  }, [schemas, initialValues]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const value = event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const newErrors: Record<string, string | undefined> = {};

    for (const schema of schemas) {
      const value = values[schema.name] ?? '';
      const validation = schema.validation;
      if (validation === null || validation === undefined) {
        continue;
      }

      const displayLabel = (labels.formLegendTranslations && labels.formLegendTranslations[schema.label]) ?? schema.label;
      const result = validation.safeParse(value);
      if (!result.success) {
        const issueMessage = result.error.issues[0]?.message;
        const normalized = normalizeFieldError(
          issueMessage ?? '',
          displayLabel,
          labels.validationFallback,
          schema.name,
          labels.invalidFormat
        );
        newErrors[schema.name] = normalized ?? labels.validationFallback;
      }
    }

    if (Object.values(newErrors).some((error) => error !== null && error !== undefined && error !== '')) {
      setErrors(newErrors);
      return;
    }

    await onSubmit(values);
  };

  return (
    <form className="flow-form" onSubmit={handleSubmit}>
      <div className="form-fields" style={{ display: 'grid', gap: '16px' }}>
        {schemas.map((schema) => {
          const legendMap = labels.formLegendTranslations;
          const hasLegendMap = legendMap != null;
          const legendTranslated = hasLegendMap && schema.label in legendMap;
          const displayLabel = legendTranslated && legendMap ? legendMap[schema.label] : schema.label;
          if (hasLegendMap && !legendTranslated && locale !== undefined) {
            reportMissingTranslation(locale, schema.label, 'legend', reportedMissingRef.current);
          }

          let displayDescription: string | undefined;
          if (schema.description) {
            const descMap = labels.formDescriptionTranslations;
            const hasDescMap = descMap != null;
            const descTranslated = hasDescMap && schema.description in descMap;
            displayDescription = descTranslated && descMap ? descMap[schema.description] : schema.description;
            if (hasDescMap && !descTranslated && locale !== undefined) {
              reportMissingTranslation(locale, schema.description, 'description', reportedMissingRef.current);
            }
          }

          return (
            <label key={schema.name} className="input-field">
              <span>{displayLabel}</span>
              {displayDescription && <span className="hint">{displayDescription}</span>}
              {renderField(schema, values[schema.name] ?? '', handleChange(schema.name), labels.selectPlaceholder)}
              {errors[schema.name] !== null && errors[schema.name] !== undefined && errors[schema.name] !== '' && <span className="error">{errors[schema.name]}</span>}
              {(errors[schema.name] === null || errors[schema.name] === undefined || errors[schema.name] === '') &&
                renderBackendError(schema.name, displayLabel, action.errors, labels)}
            </label>
          );
        })}
      </div>

      <div style={{ marginTop: '36px', display: 'flex', gap: '12px' }}>
        <button type="submit" disabled={submitting} style={{ minWidth: '160px' }}>
          {submitting ? (
            <>
              <span className="button-spinner" aria-hidden="true" />
              <span>{labels.processing}</span>
            </>
          ) : (
            action.submit_label ?? labels.submit
          )}
        </button>
      </div>
    </form>
  );
}

function renderField(
  schema: FieldSchema,
  value: string,
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void,
  placeholderFallback: string
): JSX.Element {
  if (Array.isArray(schema.options) && schema.options.length > 0) {
    return (
      <select value={value} onChange={onChange}>
        <option value="">{placeholderFallback}</option>
        {schema.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (schema.type === 'date') {
    return (
      <input
        type="date"
        value={value}
        onChange={onChange}
        required={schema.required}
        aria-label={schema.label}
      />
    );
  }

  if (schema.type === 'number') {
    return (
      <input
        type="number"
        value={value}
        placeholder={schema.placeholder ?? placeholderFallback}
        onChange={onChange}
        required={schema.required}
        step={schema.step ?? 'any'}
        inputMode="decimal"
      />
    );
  }

  return (
    <input
      type={schema.type}
      value={value}
      placeholder={schema.placeholder ?? placeholderFallback}
      onChange={onChange}
      autoComplete="one-time-code"
      required={schema.required}
    />
  );
}

function pickErrorMessage(entry: unknown): string {
  if (entry !== null && entry !== undefined && typeof entry === 'object') {
    if (Array.isArray(entry)) {
      return entry.map(pickErrorMessage).join(', ');
    }
    const record = entry as Record<string, unknown>;
    const candidates = [record.message, record.error, record.reason, record.code];
    const message = candidates.find(
      (value): value is string => typeof value === 'string' && value.trim().length > 0
    );
    if (message !== null && message !== undefined && message !== '') {
      return message;
    }
    if (record.context !== null && record.context !== undefined && typeof record.context === 'object') {
      const ctxMessage = pickErrorMessage(record.context);
      if (ctxMessage !== null && ctxMessage !== undefined && ctxMessage !== '' && ctxMessage.trim().length > 0) {
        return ctxMessage;
      }
    }
    return JSON.stringify(entry);
  }

  if (typeof entry === 'string') {
    return entry;
  }

  return String(entry);
}

function buildInitialValues(
  schemas: FieldSchema[],
  initialValues: Record<string, unknown> | undefined,
  previous?: Record<string, string>
): Record<string, string> {
  const initial: Record<string, string> = { ...(previous ?? {}) };

  const sources = collectSources(initialValues);

  for (const schema of schemas) {
    const current = initial[schema.name];
    if (typeof current === 'string' && current.trim().length > 0) {
      continue;
    }

    const value = findValueInSources(sources, schema.name);
    if (typeof value === 'string' && value.trim().length > 0) {
      initial[schema.name] = value;
      continue;
    }

    if (!(schema.name in initial)) {
      initial[schema.name] = '';
    }
  }

  return initial;
}

function collectSources(
  root: Record<string, unknown> | undefined
): Array<Record<string, unknown> | Array<unknown>> {
  const sources: Array<Record<string, unknown> | Array<unknown>> = [];

  const visit = (value: unknown): void => {
    if (value === null || value === undefined || typeof value !== 'object') {
      return;
    }
    if (sources.includes(value as any)) {
      return;
    }
    sources.push(value as Record<string, unknown> | Array<unknown>);

    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    const record = value as Record<string, unknown>;
    for (const nestedKey of ['dependencies', 'Unipesa', 'provider_extra', 'extra']) {
      visit(record[nestedKey]);
    }
  };

  visit(root);
  return sources;
}

function findValueInSources(
  sources: Array<Record<string, unknown> | Array<unknown>>,
  key: string
): string | undefined {
  for (const source of sources) {
    const found = findNestedValue(source, key);
    if (isUsableValue(found)) {
      if (typeof found === 'number') {
        return String(found);
      }
      if (typeof found === 'string') {
        return found;
      }
    }
  }
  return undefined;
}

function findNestedValue(
  source: Record<string, unknown> | Array<unknown>,
  key: string,
  visited = new Set<unknown>()
): unknown {
  if (source === null || source === undefined || typeof source !== 'object') {
    return undefined;
  }
  if (visited.has(source)) {
    return undefined;
  }
  visited.add(source);

  if (Array.isArray(source)) {
    for (const item of source) {
      if (item !== null && item !== undefined && typeof item === 'object') {
        const nested = findNestedValue(item as Record<string, unknown>, key, visited);
        if (nested !== undefined) {
          return nested;
        }
      }
    }
    return undefined;
  }

  if (key in source) {
    const direct = (source as Record<string, unknown>)[key];
    if (direct !== undefined) {
      return direct;
    }
  }

  for (const value of Object.values(source as Record<string, unknown>)) {
    if (value !== null && value !== undefined && typeof value === 'object') {
      const nested = findNestedValue(value as Record<string, unknown>, key, visited);
      if (nested !== undefined) {
        return nested;
      }
    }
  }

  return undefined;
}

function isUsableValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  return false;
}

function normalizeFieldError(
  message: string,
  label: string,
  fallback: string,
  fieldName: string,
  formatFallback?: string
): string | undefined {
  if (message === null || message === undefined || message === '') {
    return fallback;
  }

  const trimmed = message.trim();
  const formatFailPattern = /^field\s+"?([\w.\[\]]+)"?\s+format\s+fail:?/i;
  if (formatFailPattern.test(trimmed)) {
    return undefined;
  }

  const labelNormalized = label.trim();
  const labelLower = labelNormalized.toLowerCase();

  if (trimmed === labelNormalized) {
    return undefined;
  }

  const withoutPrefix = trimmed.replace(/^field\s+"?([\w.\[\]]+)"?\s*/i, '').trim();

  let candidate = withoutPrefix;

  if (candidate.toLowerCase().startsWith(labelLower)) {
    const remainder = candidate.slice(labelNormalized.length).replace(/^[:\-–\s]+/, '').trim();
    if (!remainder) {
      return undefined;
    }
    if (remainder.toLowerCase().includes('does not match format') || remainder.toLowerCase().includes('format fail')) {
      return formatFallback ?? fallback;
    }
    candidate = remainder;
  }

  if (candidate.toLowerCase().includes('does not match format') || candidate.toLowerCase().includes('format fail')) {
    return formatFallback ?? fallback;
  }

  if (candidate.toLowerCase().includes(fieldName.toLowerCase())) {
    const genericMatch = candidate.match(/"([^"]+)"\s+is\s+required/i);
    if (genericMatch) {
      return `${label}: ${fallback}`;
    }
  }

  return candidate;
}

function renderBackendError(
  fieldName: string,
  label: string,
  backendErrors: UniversalAction['errors'] | undefined,
  labels: Props['labels']
): JSX.Element | null {
  if (backendErrors === null || backendErrors === undefined || backendErrors[fieldName] === null || backendErrors[fieldName] === undefined || backendErrors[fieldName].length === 0) {
    return null;
  }

  const normalized = backendErrors[fieldName]
    ?.map(pickErrorMessage)
    .map((msg) =>
      normalizeFieldError(msg, label, labels.validationFallback, fieldName, labels.invalidFormat)
    )
    .filter((msg) => msg && msg.trim().length > 0);

  const fallbackMessage =
    labels.pending ?? `Please fill ${label.toLowerCase() || fieldName.toLowerCase()}`;
  const message =
    normalized !== null && normalized !== undefined && normalized.length > 0
      ? normalized[0]
      : fallbackMessage;

  return (
    <span className="error" style={{ opacity: 0.8 }}>
      {message}
    </span>
  );
}

